import { useEffect, useState } from 'react';
import { Box, Button, makeStyles, InputAdornment, IconButton, Grid } from '@material-ui/core';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import { auth } from '../../core/services';
import { useAppContext } from '../../core/contexts';
import { useHistory, useLocation } from 'react-router';
import { FormProvider, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Loader, SGSForm, SGSFormInput } from 'components';
import { useQueryClient } from 'react-query';
import secure from '../../assets/secure.svg';
import SmallHeader from 'features/utilisateurs/components/header';
import SmallFooter from 'features/utilisateurs/components/Footer';
import welcome from '../../assets/welcome.svg';
import classNames from 'classnames';

const { resetPassword } = auth;

const NewPassword = () => {
    const history = useHistory();
    const location = useLocation();
    const { setToken, setAppLoading, user } = useAppContext();
    const classes = useStyles();
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState(null);
    const queryClient = useQueryClient();
    const [isLoading, setIsLoading] = useState(true);

    const queryString = window.location.search;
    let token = getTokenFromURL(queryString);

    useEffect(() => {
        if (token) {
            // Validate token (already used or outdated ??)
            auth.getTokenValidity({ token })
                .catch((err) => {
                    setError(err?.message);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, []);

    type ResetForm = {
        password: string;
        confirmation: string;
    };

    // Reset is used in AppProvider to avoid logout on form submit
    const searchParams = new URLSearchParams(location.search);
    const reset = searchParams.get('reset');

    if (!user.token && !reset) {
        const newQueryString = 'token=' + token + '&reset=true';
        history.replace({ search: newQueryString });
    }

    const validationSchema: Yup.SchemaOf<ResetForm> = Yup.object().shape({
        confirmation: Yup.string()
            .required('La confirmation est obligatoire')
            .oneOf([Yup.ref('password'), null], 'La confirmation doit être identique au mot de passe'),
        password: Yup.string()
            .required('Le mot de passe est obligatoire')
            .test('password', 'Le mot de passe doit respecter les critères de sécurité', function (value) {
                if (value) {
                    return /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9]).{12,20}$/.test(value);
                }
                return true;
            })
            .test('max-length', 'Maximum 20 caractères', function (value) {
                if (value && value.length > 20) {
                    return false;
                }
                return true;
            }),
    });

    const methods = useForm<ResetForm>({
        resolver: yupResolver(validationSchema),
        defaultValues: {},
    });

    const { handleSubmit } = methods;

    const handleSendForm = (form: ResetForm) => {
        setAppLoading(true);
        resetPassword({
            token: token,
            password: form.password,
        })
            .then((response) => {
                const username = response?.username ?? null;
                if (username) {
                    auth.authenticate(username, form.password)
                        .then(async ({ token }) => {
                            // Reset token
                            await queryClient.removeQueries();
                            await queryClient.clear();
                            setToken(token);
                            history.push('/');
                        })
                        .catch(() => {
                            signin();
                        })
                        .finally(() => setAppLoading(false));
                }
            })
            .catch((error) => {
                if (error.response?.data?.message) {
                    setError(error.response?.data?.message);
                }
                setAppLoading(false);
            });
    };

    const signin = () => history.push('/connexion');

    useEffect(() => setAppLoading(false), [setAppLoading]);

    // const goToPage = () => {
    //     history.push('/connexion');
    // };
    const handleTogglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    //const goToResetPassword = () => history.push('/mdp-oublie');

    return (
        <Box className={classes.container}>
            <SmallHeader />
            {isLoading && (
                <Box style={{ marginTop: '100px' }} className={classes.row}>
                    <Loader loading={true}></Loader>
                </Box>
            )}

            {!error && !isLoading && (
                <>
                    <Box style={{ marginTop: '100px' }} className={classes.row}>
                        <Grid container justifyContent="center" style={{ marginBottom: '50px' }}>
                            <Grid item md={2} className={'p-5'}></Grid>

                            <Grid item md={5} className={classes.boxRight}>
                                <Box display={'flex'} flexDirection={'column'} alignItems={'center'}>
                                    <Box className={classes.logoSecure}>
                                        <img src={secure} />
                                    </Box>
                                    <Box className="ml-3">
                                        <Box className={classes.sousTitre}>Réinitialisation de votre Mot de Passe</Box>
                                    </Box>
                                </Box>

                                <Box className={classes.txt}>
                                    <Box>Veuillez saisir un nouveau mot de passe en respectant les critères suivants :</Box>
                                    <Box className="mt-3">
                                        <ul>
                                            <li> Entre 12 et 20 caractères </li>
                                            <li> 1 caractère spécial minimum (-!@#$%^&*)</li>
                                            <li> 1 chiffre minimum</li>
                                            <li> 1 majuscule minimum</li>
                                            <li> 1 minuscule minimum</li>
                                        </ul>
                                    </Box>
                                    <Box className="mt-2">
                                        <FormProvider {...methods}>
                                            <form className={classes.form} onSubmit={handleSubmit(handleSendForm)}>
                                                <Box className={classes.txtBlueBold}>Mot de passe :</Box>
                                                <SGSForm.Row>
                                                    <SGSForm.Col>
                                                        <SGSFormInput
                                                            type={showPassword ? 'text' : 'password'}
                                                            variant="outlined"
                                                            name="password"
                                                            InputProps={{
                                                                endAdornment: (
                                                                    <InputAdornment position="end">
                                                                        <IconButton onClick={handleTogglePasswordVisibility} className={classes.btnBlue}>
                                                                            {showPassword ? <Visibility /> : <VisibilityOff />}
                                                                        </IconButton>
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                        />
                                                    </SGSForm.Col>
                                                </SGSForm.Row>
                                                <Box className={classes.txtBlueBold}>Confirmez votre mot de passe :</Box>
                                                <SGSForm.Row>
                                                    <SGSForm.Col>
                                                        <SGSFormInput
                                                            type={showPassword ? 'text' : 'password'}
                                                            variant="outlined"
                                                            name="confirmation"
                                                            InputProps={{
                                                                endAdornment: (
                                                                    <InputAdornment position="end">
                                                                        <IconButton onClick={handleTogglePasswordVisibility} className={classes.btnBlue}>
                                                                            {showPassword ? <Visibility /> : <VisibilityOff />}
                                                                        </IconButton>
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                        />
                                                    </SGSForm.Col>
                                                </SGSForm.Row>
                                                <Button fullWidth type="submit" variant="contained" color="primary" className={classes.submit}>
                                                    CONFIRMER LE MOT DE PASSE
                                                </Button>
                                            </form>
                                        </FormProvider>
                                    </Box>
                                </Box>
                            </Grid>

                            <Grid item md={3} className={'pl-5'}>
                                <Box>
                                    <img src={welcome} />
                                </Box>
                            </Grid>

                            <Grid item md={2} className={'p-5'}></Grid>
                        </Grid>
                    </Box>
                </>
            )}
            {error && (error === 'Token is expired !' || error === 'Password already updated !') && (
                <>
                    <Box className={classes.row}>
                        <Grid container justifyContent="center">
                            <Grid item md={2} className={'p-5'}></Grid>

                            <Grid item md={5} className={classes.boxRight}>
                                <Box display={'flex'} flexDirection={'column'} alignItems={'center'}>
                                    <Box className={classes.logoSecure}>
                                        <img src={secure} />
                                    </Box>
                                    <Box className="ml-3 mt-2">
                                        <Box className={classes.sousTitre}>Le lien a expiré !</Box>
                                    </Box>
                                </Box>

                                <Box className={classNames(classes.txtBlue, 'text-center')}>
                                    <Box className="mt-4"> Un nouvel email vient de vous être envoyé. Il sera valide pendant les 15 prochaines minutes. Pensez à vérifier vos spams. </Box>
                                    <Box className="mt-4">En cas de problème, vous pouvez nous contacter à l'adresse support@sgsante.fr </Box>
                                </Box>
                            </Grid>

                            <Grid item md={3} className={'pl-5'}>
                                <Box>
                                    <img src={welcome} />
                                </Box>
                            </Grid>

                            <Grid item md={2} className={'p-5'}></Grid>
                        </Grid>
                    </Box>
                </>
            )}
            {error && (error === 'Token is missing !' || error === 'No access token found') && (
                <>
                    <Box className={classes.row}>
                        <Grid container justifyContent="center">
                            <Grid item md={2} className={'p-5'}></Grid>

                            <Grid item md={5} className={classes.boxRight}>
                                <Box display={'flex'} flexDirection={'column'} alignItems={'center'}>
                                    <Box className={classes.logoSecure}>
                                        <img src={secure} />
                                    </Box>
                                    <Box className="ml-3 mt-2">
                                        <Box className={classes.sousTitre}>Oups, une erreur est survenue ...</Box>
                                    </Box>
                                </Box>

                                <Box className={classNames(classes.txtBlue, 'text-center')}>
                                    <Box className="mt-4">Votre jeton est invalide ! </Box>
                                </Box>
                            </Grid>

                            <Grid item md={3} className={'pl-5'}>
                                <Box>
                                    <img src={welcome} />
                                </Box>
                            </Grid>

                            <Grid item md={2} className={'p-5'}></Grid>
                        </Grid>
                    </Box>
                </>
            )}

            <SmallFooter />
        </Box>
    );
};

export default NewPassword;

const useStyles = makeStyles((theme) => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
    },
    row: {
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    middleRow: {
        flex: 2,
    },
    paper: {
        paddingTop: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    avatar: {
        margin: theme.spacing(1),
        backgroundColor: theme.palette.secondary.main,
    },
    form: {
        width: '100%', // Fix IE 11 issue.
        marginTop: theme.spacing(1),
    },
    submit: {
        color: '#FFF',
        textAlign: 'center',
        fontFamily: 'Open Sans',
        fontSize: 14,
        fontStyle: 'normal',
        fontWeight: 700,
        padding: '16px',
    },
    txt: {
        color: '#4F7793',
        fontFamily: 'Open Sans',
        fontSize: '18px',
        fontStyle: 'normal',
        lineHeight: '26px',
        fontWeight: 400,
        maxWidth: '700px',
        marginTop: '30px',
    },
    txtBlueBold: {
        color: '#4F7793',
        fontFamily: 'Open Sans',
        fontSize: '18px',
        fontStyle: 'normal',
        lineHeight: '26px',
        fontWeight: 600,
        maxWidth: '700px',
        marginTop: '10px',
        marginBottom: '10px',
    },
    contactLink: {
        textDecoration: 'none', // Optional: Remove underline
        color: theme.palette.primary.main, // Optional: Use primary color
    },
    titre: {
        color: '#2574A9',
        fontFamily: 'Open Sans',
        fontSize: '36px',
        fontStyle: 'normal',
        fontWeight: 300,
        lineHeight: 'normal',
    },
    sousTitre: {
        color: '#2574A9',
        fontFamily: 'Open Sans',
        fontSize: 22,
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: 'normal',
    },
    boxRight: {
        background: '#fff',
        paddingLeft: '50px',
        paddingRight: '50px',
        borderRadius: '10px 10px 0px 0px',
        maxWidth: '582px',
        padding: '20px',
        paddingBottom: '40px',
    },
    logoSecure: {
        background: '#fff',
        borderRadius: '50%',
        marginTop: '-80px',
        padding: '20px',
    },
    txtRed: {
        color: '#EB4C37',
        fontFamily: 'Segoe UI',
        fontSize: 18,
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: 'normal',
    },
    txtGreen: {
        color: '#46BE81',
        fontFamily: 'Segoe UI',
        fontSize: 18,
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: 'normal',
    },
    txtBlue: {
        color: '#4F7793',
        fontFamily: 'Segoe UI',
        fontSize: 18,
        fontStyle: 'normal',
        fontWeight: 300,
        lineHeight: 'normal',
    },
    txtBlueUnderline: {
        color: '#4F7793',
        fontFamily: 'Segoe UI',
        fontSize: 18,
        fontStyle: 'normal',
        fontWeight: 300,
        lineHeight: 'normal',
        textDecorationLine: 'underline',
    },
    btnBlue: {
        color: '#4F7793',
    },
}));
function getTokenFromURL(url) {
    var startIndex = url.indexOf('?token=');
    if (startIndex !== -1) {
        startIndex += 7; // Length of "?token="
        var endIndex = url.indexOf('&', startIndex);
        if (endIndex === -1) {
            return url.slice(startIndex);
        } else {
            return url.slice(startIndex, endIndex);
        }
    }
    return null; // Token parameter not found
}
